import { useState, type KeyboardEvent } from "react";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, CheckCircle2, ExternalLink, Menu, RefreshCw, Store } from "lucide-react";
import type { KuaishouExperienceCategory } from "@ailan/contracts";
import { getKuaishouExperienceDashboard } from "../api/client";
import { ExperiencePlatformTabs } from "../components/ExperiencePlatformTabs";

const sourceLabels = { DEMO: "文档参考快照", LIVE: "浏览器实采", CONNECTOR: "企业同步" } as const;

function formatDateTime(value: string) {
  if (!value || value.startsWith("1970-")) return "尚未采集";
  return new Intl.DateTimeFormat("zh-CN", { month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hour12: false }).format(new Date(value));
}

function ChangeDetails({ change, previousScore, reason }: { change: string | null; previousScore: number | null; reason: string | null }) {
  return (
    <details className="douyinExperienceChange">
      <summary>{change ? `较前一日 ${change}` : "暂无前日数据"}</summary>
      {previousScore !== null && <span>前日得分：{previousScore} 分</span>}
      {reason && <em>分析：{reason}</em>}
    </details>
  );
}

function CategoryPanel({ category, selected, onSelect }: { category: KuaishouExperienceCategory; selected: boolean; onSelect: () => void }) {
  return (
    <section id={`kuaishou-category-${category.key}`} className={`douyinExperienceCategory kuaishouExperienceCategory ${selected ? "selected" : ""}`}>
      <header>
        <div><h2>{category.name}</h2><span>权重 {category.weight}%</span></div>
        <strong>{category.score}<em> 分</em></strong>
        <span>计入总分 {category.weightedScore} 分</span>
        <ChangeDetails change={category.change} previousScore={category.previousScore} reason={category.changeReason} />
      </header>
      {category.metrics.length ? (
        <div className="douyinExperienceMetricTable">
          <table><thead><tr><th>考核指标</th><th>得分</th><th>店铺表现</th><th>权重</th><th>计入分数</th><th>较前1日</th></tr></thead>
            <tbody>{category.metrics.map((metric) => <tr key={metric.key}><td><strong>{metric.name}</strong><small>{metric.standard}</small></td><td className={metric.warning ? "warningText" : "douyinExperienceGood"}>{metric.score}分</td><td>{metric.value}</td><td>{metric.weight}%</td><td>{metric.weightedScore}分</td><td><ChangeDetails change={metric.change} previousScore={metric.previousScore} reason={metric.changeReason} /></td></tr>)}</tbody>
          </table>
        </div>
      ) : <div className="douyinExperienceCategoryEmpty">当前文档提供该窗口总分；真实采集后将在这里展示每项指标、昨日对比和原因分析</div>}
    </section>
  );
}

export function KuaishouExperiencePage({ onMenuOpen }: { onMenuOpen: () => void }) {
  const [selectedStoreName, setSelectedStoreName] = useState<string | null>(null);
  const [selectedWindow, setSelectedWindow] = useState("overall");
  const dashboard = useQuery({ queryKey: ["kuaishou-experience-dashboard"], queryFn: getKuaishouExperienceDashboard, refetchInterval: 60_000, refetchOnWindowFocus: true });

  if (dashboard.isLoading) return <main className="main"><div className="centerState"><RefreshCw className="spin" />正在读取快手体验分...</div></main>;
  if (dashboard.isError || !dashboard.data) return <main className="main"><div className="centerState errorState"><AlertTriangle />无法读取快手体验分<button className="button" onClick={() => void dashboard.refetch()}>重新加载</button></div></main>;

  const stores = dashboard.data.storeDirectory;
  const activeStoreName = selectedStoreName ?? dashboard.data.windows[0]?.storeName ?? stores[0]?.storeName ?? null;
  const scoreWindow = dashboard.data.windows.find((item) => item.storeName === activeStoreName);
  const realWindowCount = dashboard.data.windows.filter((item) => item.source !== "DEMO").length;
  const isReference = scoreWindow?.source === "DEMO";
  const focusWindow = (key: string) => {
    setSelectedWindow(key);
    if (key !== "overall") {
      window.requestAnimationFrame(() => document.getElementById(`kuaishou-category-${key}`)?.scrollIntoView({ behavior: "smooth", block: "start" }));
    }
  };
  const activateWindow = (key: string) => (event: KeyboardEvent<HTMLElement>) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      focusWindow(key);
    }
  };

  return (
    <main className="main experienceMain douyinExperienceMain kuaishouExperienceMain">
      <header className="topbar"><button className="menuButton" aria-label="打开导航" onClick={onMenuOpen}><Menu /></button><strong>店铺后台体验分</strong><span>快手店铺体验分</span><i className={realWindowCount ? "liveDot" : "offlineDot"} /><small>{realWindowCount ? "真实采集窗口已连接" : "展示文档快照，等待真实采集"}</small></header>
      <div className="experienceContent">
        <ExperiencePlatformTabs current="kuaishou" />
        <section className="douyinExperienceStoreDirectory">
          <header><div><strong>快手店铺窗口</strong><span>目录 {stores.length} 家店铺，真实数据 {realWindowCount} 家</span></div><small>逐店选择后查看店铺体验分、商品、物流和售后窗口</small></header>
          <label className="douyinExperienceStoreSelect"><Store /><span>店铺</span><select value={activeStoreName ?? ""} onChange={(event) => setSelectedStoreName(event.target.value || null)} disabled={!stores.length}>{stores.length ? stores.map((store) => {
            const captured = dashboard.data.windows.find((item) => item.storeName === store.storeName);
            const suffix = captured?.source === "DEMO" ? " · 文档快照" : captured ? " · 已采集" : " · 待采集";
            return <option key={store.id} value={store.storeName}>{store.storeName}{suffix}</option>;
          }) : <option value="">暂无店铺目录</option>}</select></label>
        </section>

        <section className="experienceHead">
          <div><h1>快手店铺体验分</h1><p>{activeStoreName ?? "暂未选择店铺"} · {scoreWindow ? `更新至 ${formatDateTime(scoreWindow.collectedAt)}` : "待采集真实数据"}</p></div>
          <div className="experienceActions">
            <button className="button" onClick={() => window.open("/#experience-kuaishou", "_blank", "noopener,noreferrer")}><ExternalLink />新窗口打开</button>
            <button className="button primary kuaishouExperiencePrimary" disabled={dashboard.isFetching} onClick={() => void dashboard.refetch()}><RefreshCw className={dashboard.isFetching ? "spin" : ""} />{dashboard.isFetching ? "刷新中..." : "刷新体验分"}</button>
          </div>
        </section>

        <section className={`experienceSyncBand ${realWindowCount ? "connected" : ""}`}>
          {realWindowCount ? <CheckCircle2 /> : <AlertTriangle />}<div><strong>{realWindowCount ? "快手真实体验分采集运行中" : "当前先展示交接文档中的快手体验分"}</strong><span>{dashboard.data.sync.message}</span></div><span>每日更新</span>
        </section>

        {scoreWindow ? <>
          <section className="douyinExperienceStore"><Store /><div><strong>{scoreWindow.storeName}</strong><span>{scoreWindow.industry ? `考核行业：${scoreWindow.industry}` : scoreWindow.accountName}{scoreWindow.validOrderCount !== null ? ` · 近30天有效订单 ${scoreWindow.validOrderCount.toLocaleString("zh-CN")}单` : ""}</span></div><em className={scoreWindow.source.toLowerCase()}>{sourceLabels[scoreWindow.source]}</em><small>平台更新时间：{scoreWindow.updatedAt}</small></section>
          {isReference && <section className="kuaishouReferenceNotice"><AlertTriangle /><div><strong>这是文档中已有的真实页面快照</strong><span>仅该店铺有截图分数；其他 14 家店铺不编造数据，接入采集后会逐店自动展示。</span></div></section>}
          <section className="douyinExperienceScoreBand kuaishouExperienceScoreBand">
            <article className={`overall ${selectedWindow === "overall" ? "selected" : ""}`} role="button" tabIndex={0} aria-pressed={selectedWindow === "overall"} onClick={() => focusWindow("overall")} onKeyDown={activateWindow("overall")}><span>店铺体验分</span><strong>{scoreWindow.overallScore}<em> 分</em></strong><small>满分 5 分</small><ChangeDetails change={scoreWindow.overallChange} previousScore={scoreWindow.previousScore} reason={scoreWindow.changeReason} /></article>
            {scoreWindow.categories.map((category) => <article key={category.key} className={selectedWindow === category.key ? "selected" : ""} role="button" tabIndex={0} aria-pressed={selectedWindow === category.key} onClick={() => focusWindow(category.key)} onKeyDown={activateWindow(category.key)}><span>{category.name}</span><strong>{category.score}<em> 分</em></strong><small>× {category.weight}% = {category.weightedScore}分</small><ChangeDetails change={category.change} previousScore={category.previousScore} reason={category.changeReason} /></article>)}
          </section>
          {scoreWindow.categories.map((category) => <CategoryPanel key={category.key} category={category} selected={selectedWindow === category.key} onSelect={() => focusWindow(category.key)} />)}
        </> : <section className="experienceEmpty"><Store /><strong>{activeStoreName ?? "还没有快手体验分数据"}</strong><span>该店铺已保留在 15 家目录中，采集窗口写入后会展示四个体验分窗口及每日变化原因</span></section>}
      </div>
    </main>
  );
}
